import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Icon } from '@/shared/icons';
import { BillingService } from '../services';
import { useCreditPaymentSocket } from '../hooks';
import { PixQrCodeDisplay } from './PixQrCodeDisplay';

interface PixQrCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modal de pagamento PIX pendente — Modal shared (F72).
 */
export const PixQrCodeModal: React.FC<PixQrCodeModalProps> = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [pixData, setPixData] = useState<{ qrCode: string; copyPaste: string; expiresAt: string } | null>(null);
  const { confirmedPayment, resetConfirmedPayment } = useCreditPaymentSocket();

  const handleClose = () => {
    resetConfirmedPayment();
    setPixData(null);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setPixData(null);
      return;
    }

    const fetchPixQrCode = async () => {
      setLoading(true);
      try {
        const result = await BillingService.getPixQrCode();
        setPixData(result);
      } catch {
        toast.error('Erro ao carregar QR Code PIX');
        onClose();
      } finally {
        setLoading(false);
      }
    };

    fetchPixQrCode();
  }, [isOpen, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="md">
      <div data-testid="pix-qrcode-modal">
        <ModalHeader title="Pagamento PIX" onClose={handleClose}>
          <Icon name="smartphone" className="h-5 w-5 flex-shrink-0" style={{ color: '#8b5cf6' }} />
        </ModalHeader>

        <ModalContent className="px-6 py-5" padding={false}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div
                className="animate-spin rounded-full h-8 w-8 border-b-2"
                style={{ borderColor: '#8b5cf6' }}
              />
            </div>
          ) : confirmedPayment ? (
            <div className="space-y-4 text-center" data-testid="pix-payment-confirmed">
              <div className="flex justify-center">
                <Icon name="check-circle" className="h-16 w-16" style={{ color: '#22c55e' }} />
              </div>
              <h3 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
                Pagamento confirmado!
              </h3>
              <p className="text-sm" style={{ color: '#6b7280' }}>
                {confirmedPayment.creditsPerCycle.toLocaleString('pt-BR')} créditos foram adicionados ao seu saldo.
              </p>
            </div>
          ) : pixData ? (
            <PixQrCodeDisplay
              qrCode={pixData.qrCode}
              copyPaste={pixData.copyPaste}
              expiresAt={pixData.expiresAt}
            />
          ) : null}
        </ModalContent>

        <ModalFooter>
          <Button variant="primary" onClick={handleClose} data-testid="pix-qrcode-close">
            Fechar
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

PixQrCodeModal.displayName = 'PixQrCodeModal';
